import { EncoderDecoder } from '@iotize/common/converter/api';
import { MemoryInfo, TargetService } from '@iotize/tap/service/impl/target';

import { BLANK_CONVERTER } from '../../utility/blank-converter';
import { KeyTypeType } from '../../utility/editable-data-stream';
import { variableFormatToByteSize } from '../../utility/type-converter-factory';
import { AbstractVariable } from '../abstract-variable-stream';

type SubTargetService = Pick<TargetService, 'readAddress' | 'writeAddress'>;

export namespace TargetMemoryVariable {
  export interface InstanceOptions<DataType = Uint8Array> {
    converter?: EncoderDecoder<DataType, Uint8Array>;
    targetService: SubTargetService;
    config: MemoryInfo;
  }
}

export class TargetMemoryVariable<
  DataType,
  KeyType extends KeyTypeType,
> extends AbstractVariable<DataType, KeyType> {
  get converter() {
    return this.context.converter || BLANK_CONVERTER;
  }

  get config() {
    return this.context.config;
  }

  constructor(
    public id: KeyType,
    private context: TargetMemoryVariable.InstanceOptions<DataType>,
  ) {
    super();
  }

  async readRawInner(): Promise<Uint8Array> {
    return (
      await this.context.targetService.readAddress(this.context.config)
    ).body();
  }

  async writeRaw(value: Uint8Array): Promise<void> {
    if (!value || !(value instanceof Uint8Array)) {
      throw new Error(
        `Bad parameter for TargetMemoryVariable.writeRaw(). Expecting an Uint8Array.`,
      );
    }
    const expectedByteSize =
      this.config.length * variableFormatToByteSize(this.config.format);
    if (value.length !== expectedByteSize) {
      // TODO better error
      throw new Error(
        `Encoded value does not have the expected byte size. Expecting ${expectedByteSize} byte(s) but given ${value.length} byte(s)`,
      );
    }
    return (
      await this.context.targetService.writeAddress({
        data: value,
        info: this.context.config,
      })
    ).body();
  }
}
